"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const DropdownContext = createContext({ close: () => {} });

const variantStyles = {
  primary:
    "bg-lightYellow text-darkBlack border-lightYellow hover:bg-darkYellow hover:border-darkYellow",
  secondary:
    "bg-lightCyan text-darkBlack border-lightCyan2 hover:bg-strongCyan hover:border-strongCyan2",
  danger:
    "bg-darkRed text-white border-darkRed hover:bg-darkRed2 hover:border-darkRed2",
  light:
    "bg-white text-darkBlack border-lightGrey3 hover:bg-lightGrey hover:border-darkGrey",
  default:
    "bg-cyanGrey text-darkBlack border-darkGrey hover:bg-darkGrey2 hover:border-darkGrey",
};

const sizeStyles = {
  sm: "text-sm py-1 px-2",
  md: "py-2 px-3",
  lg: "py-2 px-4 font-bold",
};

/**
 * Dropdown - Container for dropdown menu structure.
 */
function DropdownComponent({ children, className = "", ...props }) {
  return (
    <div className={`relative inline-block ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}

/**
 * Dropdown.Item - A single clickable item in the dropdown menu.
 */
function DropdownItem({
  children,
  onClick,
  active = false,
  disabled = false,
  className = "",
  ...props
}) {
  const { close } = useContext(DropdownContext);

  const handleClick = (e) => {
    if (disabled) return;
    if (onClick) onClick(e);
    close();
  };

  return (
    <button
      type="button"
      className={`block w-full text-left px-4 py-1.5 text-sm transition-colors duration-100 ${
        active ? "bg-lightCyan/20 text-darkBlue" : "text-darkBlack"
      } ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-lightGrey hover:text-darkBlack"
      } ${className}`.trim()}
      onClick={handleClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Dropdown.Divider - Visual separator between items.
 */
function DropdownDivider({ className = "", ...props }) {
  return (
    <hr className={`my-1 border-lightGrey3 ${className}`.trim()} {...props} />
  );
}

/**
 * Dropdown.Menu - The dropdown menu container.
 */
function DropdownMenu({ children, show = true, className = "", ...props }) {
  if (!show) return null;
  return (
    <div
      className={`absolute z-50 mt-1 min-w-[10rem] py-2 bg-white border border-lightGrey3 rounded shadow-lg ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Dropdown.Toggle - Button that toggles the dropdown.
 */
function DropdownToggle({ children, onClick, className = "", as: AsComponent, ...props }) {
  if (AsComponent) {
    return (
      <AsComponent onClick={onClick} className={className} {...props}>
        {children}
      </AsComponent>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className} {...props}>
      {children}
    </button>
  );
}

export const Dropdown = Object.assign(DropdownComponent, {
  Item: DropdownItem,
  Divider: DropdownDivider,
  Menu: DropdownMenu,
  Toggle: DropdownToggle,
});

/**
 * DropdownButton - Convenience component: a button + dropdown in one.
 *
 * @param {string} title - Button label
 * @param {string} variant - 'primary' | 'secondary' | 'danger' | 'light' | 'default'
 * @param {string} size - 'sm' | 'md' | 'lg'
 */
export function DropdownButton({
  title,
  variant = "default",
  size = "md",
  children,
  className = "",
  style,
  ...props
}) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const variantClass = variantStyles[variant] ?? variantStyles.default;
  const sizeClass = sizeStyles[size] ?? sizeStyles.md;

  return (
    <DropdownContext.Provider value={{ close: () => setShow(false) }}>
      <div
        ref={ref}
        className={`relative inline-block ${className}`.trim()}
        style={style}
        {...props}
      >
        <button
          type="button"
          className={`inline-flex items-center justify-center font-medium rounded cursor-pointer transition-colors duration-150 border ${variantClass} ${sizeClass}`.trim()}
          onClick={() => setShow((s) => !s)}
        >
          {title}
          <svg
            className="ml-1.5 w-3 h-3 fill-current"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1.5 5.5l6.5 6.5 6.5-6.5h-13z" />
          </svg>
        </button>
        {show && (
          <div className="absolute left-0 z-50 mt-1 min-w-[10rem] py-2 bg-white border border-lightGrey3 rounded shadow-lg">
            {children}
          </div>
        )}
      </div>
    </DropdownContext.Provider>
  );
}
